import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Globe, Waves, Users, Award, CheckCircle, MapPin, Package, Building2, ChevronLeft, ChevronDown, ArrowRight, X } from "lucide-react";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
import WaveDivider from "@/components/WaveDivider";
import { useTheme } from "@/contexts/ThemeContext";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Color mapping for category colors
const COLOR_CLASSES = {
  cyan: { bg: "bg-cyan-500", hover: "hover:bg-cyan-600", border: "border-cyan-400", text: "text-cyan-600" },
  emerald: { bg: "bg-emerald-500", hover: "hover:bg-emerald-600", border: "border-emerald-400", text: "text-emerald-600" },
  purple: { bg: "bg-purple-500", hover: "hover:bg-purple-600", border: "border-purple-400", text: "text-purple-600" },
  amber: { bg: "bg-amber-500", hover: "hover:bg-amber-600", border: "border-amber-400", text: "text-amber-600" },
  slate: { bg: "bg-slate-500", hover: "hover:bg-slate-600", border: "border-slate-400", text: "text-slate-600" },
  rose: { bg: "bg-rose-500", hover: "hover:bg-rose-600", border: "border-rose-400", text: "text-rose-600" },
  blue: { bg: "bg-blue-500", hover: "hover:bg-blue-600", border: "border-blue-400", text: "text-blue-600" },
  green: { bg: "bg-green-500", hover: "hover:bg-green-600", border: "border-green-400", text: "text-green-600" },
  orange: { bg: "bg-orange-500", hover: "hover:bg-orange-600", border: "border-orange-400", text: "text-orange-600" },
  pink: { bg: "bg-pink-500", hover: "hover:bg-pink-600", border: "border-pink-400", text: "text-pink-600" },
  indigo: { bg: "bg-indigo-500", hover: "hover:bg-indigo-600", border: "border-indigo-400", text: "text-indigo-600" },
  teal: { bg: "bg-teal-500", hover: "hover:bg-teal-600", border: "border-teal-400", text: "text-teal-600" }
};

const Packages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationIdFilter = searchParams.get('location_id');
  const diveCenterIdFilter = searchParams.get('dive_center_id');
  const { pageStyles } = useTheme();
  
  // Theme detection for Packages page
  const packagesBody = pageStyles?.packages?.body;
  const isDarkTheme = packagesBody?.text_color === '#e2e8f0' || packagesBody?.text_color === '#ffffff';
  const themeAccent = packagesBody?.accent_color || '#0891b2';
  const themeBorder = packagesBody?.border_color || 'rgba(229,231,235,1)';
  
  const [currentLocationInfo, setCurrentLocationInfo] = useState(null);
  const [locationDiveCenters, setLocationDiveCenters] = useState([]);
  const [selectedDiveCenter, setSelectedDiveCenter] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedDiveSite, setSelectedDiveSite] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // George Dives Specials (location-level)
  const [locationSpecials, setLocationSpecials] = useState([]);
  const [specialsExpanded, setSpecialsExpanded] = useState(false);  // Collapsed by default per user request
  const [showSpecialsOnly, setShowSpecialsOnly] = useState(false);
  const [currentLocationId, setCurrentLocationId] = useState(null);
  
  const handleBookPackage = () => {
    if (selectedDiveSite) {
      sessionStorage.setItem('bookingDiveSite', JSON.stringify(selectedDiveSite));
    }
    if (selectedDiveCenter) {
      sessionStorage.setItem('bookingDiveCenter', JSON.stringify({
        id: selectedDiveCenter.id,
        name: selectedDiveCenter.name,
        location: selectedDiveCenter.location,
        country: selectedDiveCenter.country
      }));
    }
    if (selectedPackage) {
      sessionStorage.setItem('bookingPackage', JSON.stringify(selectedPackage));
    }
    navigate('/contact');
  };

  // Get parent categories (no parent_id)
  const getParentCategories = () => {
    return packageData?.categories?.filter(cat => !cat.parent_id) || [];
  };

  // Get subcategories for a parent
  const getSubcategories = (parentKey) => {
    const parent = packageData?.categories?.find(cat => cat.key === parentKey);
    if (!parent) return [];
    return packageData?.categories?.filter(cat => cat.parent_id === parent.id) || [];
  };

  // Load packages for a specific dive center
  const loadPackagesForCenter = async (centerId) => {
    try {
      const packagesRes = await axios.get(`${API}/dive-center-packages/${centerId}`).catch(() => ({ data: null }));
      if (packagesRes.data) {
        setPackageData(packagesRes.data);
        const firstParent = packagesRes.data.categories?.find(c => !c.parent_id);
        if (firstParent) {
          setActiveCategory(firstParent.key);
          const subcats = packagesRes.data.categories?.filter(cat => cat.parent_id === firstParent.id) || [];
          if (subcats.length > 0) {
            setActiveSubcategory(subcats[0].key);
          } else {
            setActiveSubcategory(null);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  // Select a dive center and load its packages
  const handleSelectDiveCenter = async (center) => {
    setSelectedDiveCenter(center);
    setPackageData(null);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setSelectedPackage(null);
    await loadPackagesForCenter(center.id);
  };

  // Go back to dive center selection
  const handleBackToCenters = () => {
    setSelectedDiveCenter(null);
    setPackageData(null);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setSelectedPackage(null);
    setShowSpecialsOnly(false);
  };

  // Fetch location specials
  const fetchLocationSpecials = async (locationId) => {
    if (!locationId) return;
    try {
      const res = await axios.get(`${API}/location-specials/${locationId}`);
      setLocationSpecials(res.data.items || []);
    } catch {
      setLocationSpecials([]);
    }
  };

  // Select "George Dives Specials" standalone view
  const handleSelectSpecials = () => {
    setSelectedDiveCenter(null);
    setPackageData(null);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setSelectedPackage(null);
    setShowSpecialsOnly(true);
  };

  useEffect(() => {
    const storedDiveSite = sessionStorage.getItem('selectedDiveSite');
    if (storedDiveSite) {
      setSelectedDiveSite(JSON.parse(storedDiveSite));
    }
    
    const fetchData = async () => {
      setLoading(true);
      try {
        if (diveCenterIdFilter) {
          // Direct dive center link — load that center's packages directly
          const centerRes = await axios.get(`${API}/map/dive-centers/${diveCenterIdFilter}`).catch(() => ({ data: null }));
          if (centerRes.data) {
            setSelectedDiveCenter(centerRes.data);
            // Also set location info
            if (centerRes.data.location_id) {
              setCurrentLocationId(centerRes.data.location_id);
              const locRes = await axios.get(`${API}/locations/${centerRes.data.location_id}`).catch(() => ({ data: null }));
              if (locRes.data) setCurrentLocationInfo(locRes.data);
              // Fetch sibling centers
              const siblingRes = await axios.get(`${API}/locations/${centerRes.data.location_id}/dive-centers`).catch(() => ({ data: [] }));
              setLocationDiveCenters(siblingRes.data || []);
              fetchLocationSpecials(centerRes.data.location_id);
            } else {
              setCurrentLocationInfo({ name: centerRes.data.location, country: centerRes.data.country });
            }
            await loadPackagesForCenter(diveCenterIdFilter);
          }
        } else if (locationIdFilter) {
          // Location link — show location's dive centers
          setCurrentLocationId(locationIdFilter);
          const locRes = await axios.get(`${API}/locations/${locationIdFilter}`).catch(() => ({ data: null }));
          if (locRes.data) {
            setCurrentLocationInfo(locRes.data);
            const centersRes = await axios.get(`${API}/locations/${locationIdFilter}/dive-centers`).catch(() => ({ data: [] }));
            const centers = centersRes.data || [];
            setLocationDiveCenters(centers);
            fetchLocationSpecials(locationIdFilter);
            // If only one dive center, auto-select it
            if (centers.length === 1) {
              setSelectedDiveCenter(centers[0]);
              await loadPackagesForCenter(centers[0].id);
            }
          }
        } else {
          // Default: use current location from timeline
          const timelineRes = await axios.get(`${API}/timeline/home-view`).catch(() => ({ data: null }));
          
          if (timelineRes.data && timelineRes.data.months && timelineRes.data.months.length > 0) {
            const currentMonth = timelineRes.data.months.find(m => m.is_current) || timelineRes.data.months[0];
            
            if (currentMonth.location_id) {
              setCurrentLocationId(currentMonth.location_id);
              // New hierarchy flow: location → dive centers
              const locRes = await axios.get(`${API}/locations/${currentMonth.location_id}`).catch(() => ({ data: null }));
              if (locRes.data) setCurrentLocationInfo(locRes.data);
              
              const centersRes = await axios.get(`${API}/locations/${currentMonth.location_id}/dive-centers`).catch(() => ({ data: [] }));
              const centers = centersRes.data || [];
              setLocationDiveCenters(centers);
              fetchLocationSpecials(currentMonth.location_id);
              
              // If only one dive center, auto-select it
              if (centers.length === 1) {
                setSelectedDiveCenter(centers[0]);
                await loadPackagesForCenter(centers[0].id);
              }
            } else if (currentMonth.dive_center_id) {
              // Legacy fallback: direct dive center
              const centerRes = await axios.get(`${API}/map/dive-centers/${currentMonth.dive_center_id}`).catch(() => ({ data: null }));
              if (centerRes.data) {
                setSelectedDiveCenter(centerRes.data);
                setCurrentLocationInfo({ name: centerRes.data.location, country: centerRes.data.country });
                setLocationDiveCenters([centerRes.data]);
                await loadPackagesForCenter(currentMonth.dive_center_id);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [diveCenterIdFilter, locationIdFilter]);

  return (
    <>
      <SEO 
        title="Diving Packages & Courses"
        description="Explore our comprehensive diving packages, certification courses, and special offers. From beginner to advanced, find the perfect underwater adventure."
        keywords="diving packages, scuba courses, PADI certification, dive trips, underwater adventures"
      />
      <Navigation />
      <FloatingUserMenu />

      <HeroSection 
        title="Dive Packages,"
        subtitle="For Every Level"
        description="From beginner certifications to advanced training, find the perfect diving program for your next adventure."
        backgroundColor="from-blue-600 via-purple-600 to-indigo-700"
      />

      <WaveDivider variant="smooth" />

      {/* Location & Dive Center Info Section */}
      <section className="py-8 bg-slate-900" data-testid="packages-info-section">
        <div className="container mx-auto px-4 relative z-10">
          {/* Location Info Banner */}
          {currentLocationInfo && (
            <div className="mt-4 max-w-4xl mx-auto" data-testid="location-info-banner">
              <div className="bg-white/10 backdrop-blur-md border-2 border-cyan-400/50 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm text-cyan-200 font-semibold mb-1">
                        {selectedDiveCenter ? 'Viewing packages from:' : 'Current Location:'}
                      </p>
                      {selectedDiveCenter ? (
                        <>
                          <p className="text-xl sm:text-2xl text-white font-bold">{selectedDiveCenter.name}</p>
                          <div className="flex items-center gap-2 text-cyan-300 mt-1">
                            <MapPin className="w-4 h-4" />
                            <p className="text-sm">{currentLocationInfo.name}, {currentLocationInfo.country}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-xl sm:text-2xl text-white font-bold">{currentLocationInfo.name}</p>
                          <p className="text-sm text-cyan-300">{currentLocationInfo.country}</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {selectedDiveCenter && locationDiveCenters.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white/10 hover:bg-white/20 text-white border-white/30 whitespace-nowrap"
                        onClick={handleBackToCenters}
                        data-testid="back-to-centers-btn"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Other Centers
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white/10 hover:bg-white/20 text-white border-white/30 whitespace-nowrap"
                      onClick={() => navigate('/dive-centers')}
                      data-testid="browse-all-centers-btn"
                    >
                      Browse All
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Selected Dive Site Banner */}
          {selectedDiveSite && (
            <div className="mt-8 max-w-2xl mx-auto">
              <div className="bg-gradient-to-r from-green-500/20 to-teal-500/20 backdrop-blur-md border-2 border-green-400/50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Waves className="w-5 h-5 text-green-300" />
                  <div className="text-left">
                    <p className="text-sm text-green-200 font-semibold">Selected Dive Site:</p>
                    <p className="text-lg text-white font-bold">{selectedDiveSite.name}</p>
                    <p className="text-xs text-green-200/80 mt-1">This will be included in your booking inquiry</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                  onClick={() => {
                    setSelectedDiveSite(null);
                    sessionStorage.removeItem('selectedDiveSite');
                  }}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Dive Center Selection Cards — shown when location has multiple centers and none selected */}
      {!selectedDiveCenter && !showSpecialsOnly && !loading && locationDiveCenters.length > 1 && (
        <section className="py-12" style={{ backgroundColor: isDarkTheme ? '#161025' : '#f9fafb' }} data-testid="dive-center-selection">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2" style={{ color: isDarkTheme ? '#e2e8f0' : '#111827' }}>Choose a Dive Center</h2>
              <p style={{ color: isDarkTheme ? '#94a3b8' : '#4b5563' }}>
                {currentLocationInfo?.name} has {locationDiveCenters.length} partner dive centers. Select one to view their packages.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locationDiveCenters.map((center) => (
                <Card 
                  key={center.id} 
                  className="overflow-hidden cursor-pointer hover:shadow-xl transition-all border-2 border-transparent group"
                  style={{ 
                    backgroundColor: isDarkTheme ? '#1c1533' : '#ffffff',
                    borderColor: 'transparent'
                  }}
                  onClick={() => handleSelectDiveCenter(center)}
                  data-testid={`dive-center-card-${center.id}`}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = isDarkTheme ? themeAccent : '#22d3ee'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                >
                  {center.image && (
                    <div className="h-32 bg-gradient-to-r from-cyan-600 to-blue-700 flex items-center justify-center p-4">
                      <img 
                        src={center.image} 
                        alt={center.name}
                        className="h-full w-auto object-contain"
                      />
                    </div>
                  )}
                  {!center.image && (
                    <div className="h-32 bg-gradient-to-r from-cyan-600 to-blue-700 flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-white/60" />
                    </div>
                  )}
                  <CardContent className="p-5">
                    <h3 className="text-lg font-bold mb-1" style={{ color: isDarkTheme ? '#e2e8f0' : '#111827' }}>{center.name}</h3>
                    <p className="text-sm mb-3" style={{ color: isDarkTheme ? '#94a3b8' : '#6b7280' }}>{center.short_description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm" style={{ color: themeAccent }}>
                        <MapPin className="w-4 h-4" />
                        {center.location}
                      </div>
                      <ArrowRight className="w-5 h-5 transition-colors" style={{ color: isDarkTheme ? '#64748b' : '#9ca3af' }} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* George Dives Specials — full-width collapsible section AFTER all dive centers */}
            {locationSpecials.length > 0 && (
              <div
                className="mt-6"
                data-testid="specials-collapsible-section"
              >
                <button
                  type="button"
                  onClick={() => setSpecialsExpanded(prev => !prev)}
                  className="w-full overflow-hidden rounded-lg border-2 border-amber-300 hover:border-amber-400 transition-all flex items-center gap-3 p-3 text-left"
                  style={{ backgroundColor: isDarkTheme ? '#1c1533' : '#fff' }}
                  data-testid="specials-collapsible-trigger"
                  aria-expanded={specialsExpanded}
                >
                  <div className="w-12 h-12 rounded-md bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold" style={{ color: isDarkTheme ? '#e2e8f0' : '#111827' }}>
                      George Dives Specials
                    </div>
                    <div className="text-xs" style={{ color: isDarkTheme ? '#94a3b8' : '#6b7280' }}>
                      {locationSpecials.length} exclusive offer{locationSpecials.length !== 1 ? 's' : ''} at {currentLocationInfo?.name}
                    </div>
                  </div>
                  <ChevronDown
                    className="w-5 h-5 text-amber-600 transition-transform flex-shrink-0"
                    style={{ transform: specialsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>
                {specialsExpanded && (
                  <Card
                    className="mt-3 overflow-hidden cursor-pointer hover:shadow-xl transition-all border-2 border-transparent group"
                    style={{ backgroundColor: isDarkTheme ? '#1c1533' : '#ffffff' }}
                    onClick={handleSelectSpecials}
                    data-testid="specials-card"
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#f59e0b'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <div className="h-32 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 flex items-center justify-center relative">
                      <Award className="w-14 h-14 text-white/80" />
                      <div className="absolute bottom-2 left-0 right-0 text-center">
                        <span className="text-xs text-white/70 font-medium tracking-wider uppercase">Exclusive Offers</span>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-lg font-bold mb-1" style={{ color: isDarkTheme ? '#e2e8f0' : '#111827' }}>George Dives Specials</h3>
                      <p className="text-sm mb-3" style={{ color: isDarkTheme ? '#94a3b8' : '#6b7280' }}>Special packages available at {currentLocationInfo?.name}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-amber-600 text-sm">
                          <Award className="w-4 h-4" />
                          {locationSpecials.length} special{locationSpecials.length !== 1 ? 's' : ''}
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-amber-500 transition-colors" />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* George Dives Specials — standalone view */}
      {showSpecialsOnly && !loading && (
        <section className="py-16" data-testid="specials-standalone-section">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold" style={{ color: isDarkTheme ? '#e2e8f0' : '#111827' }}>George Dives Specials</h2>
                <p className="mt-1" style={{ color: isDarkTheme ? '#94a3b8' : '#6b7280' }}>Exclusive offers at {currentLocationInfo?.name}</p>
              </div>
              {locationDiveCenters.length > 1 && (
                <Button variant="outline" onClick={handleBackToCenters} data-testid="back-from-specials-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> All Options
                </Button>
              )}
            </div>

            <Card className="border-2 border-amber-400 shadow-xl" style={{ backgroundColor: isDarkTheme ? "#1c1533" : "#ffffff" }}>
              <CardHeader>
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <CardTitle className="text-2xl">George Dives Specials</CardTitle>
                <CardDescription>{locationSpecials.length} offer{locationSpecials.length !== 1 ? 's' : ''} available</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {locationSpecials.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedPackage({
                      id: item.id,
                      name: item.name,
                      description: item.description || item.short_description,
                      price_euro: item.price || item.price_single || item.amount,
                      ...item
                    })}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      selectedPackage?.id === item.id
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-gray-200 hover:border-amber-400 hover:bg-gray-50'
                    }`}
                    data-testid={`special-item-${item.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                        {item.description && <p className="text-gray-600 text-sm mb-2">{item.description}</p>}
                        {item.duration && <p className="text-gray-500 text-sm">Duration: {item.duration}</p>}
                      </div>
                      <div className="text-right ml-4">
                        {item.price && <p className="text-2xl font-bold text-amber-600">&euro;{item.price}</p>}
                        {item.price_single && <p className="text-xl font-bold text-amber-600">&euro;{item.price_single} <span className="text-sm text-gray-500">single</span></p>}
                        {item.price_double && <p className="text-lg font-semibold text-amber-600">&euro;{item.price_double} <span className="text-sm text-gray-500">double</span></p>}
                      </div>
                    </div>
                    {(item.full_description || item.included?.length > 0 || item.prerequisites || item.notes) && (
                      <p className="text-xs mt-2 text-amber-600 font-medium">Click for details</p>
                    )}
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Specials Package Detail Modal */}
            {selectedPackage && (
              <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={() => setSelectedPackage(null)} data-testid="specials-detail-modal-overlay">
                <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col border-t-4 border-amber-400" onClick={e => e.stopPropagation()} data-testid="specials-detail-modal">
                  <div className="shrink-0 p-5 border-b flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{selectedPackage.name}</h2>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {selectedPackage.price_euro && <span className="text-2xl font-bold text-amber-600">&euro;{selectedPackage.price_euro}</span>}
                        {selectedPackage.price_single && <span className="text-lg font-bold text-amber-600">&euro;{selectedPackage.price_single} <span className="text-sm text-gray-500">single</span></span>}
                        {selectedPackage.price_double && <span className="text-lg font-semibold text-amber-600">&euro;{selectedPackage.price_double} <span className="text-sm text-gray-500">double</span></span>}
                        {selectedPackage.duration && <span className="text-sm text-gray-500 border-l pl-3">{selectedPackage.duration}</span>}
                      </div>
                    </div>
                    <button onClick={() => setSelectedPackage(null)} className="p-1.5 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5">
                    {selectedPackage.full_description && <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedPackage.full_description}</p>}
                    {!selectedPackage.full_description && selectedPackage.description && <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedPackage.description}</p>}
                    {selectedPackage.num_dives && (
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                        <Waves className="w-5 h-5 text-blue-500" />
                        <span className="text-sm text-blue-800">Includes <strong>{selectedPackage.num_dives} dives</strong></span>
                      </div>
                    )}
                    {selectedPackage.included && selectedPackage.included.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> What's Included</h4>
                        <ul className="space-y-1.5">{selectedPackage.included.map((item, i) => <li key={i} className="text-gray-600 flex items-start gap-2 text-sm"><span className="text-green-500 mt-0.5">&#x2713;</span><span>{item}</span></li>)}</ul>
                      </div>
                    )}
                    {selectedPackage.not_included && selectedPackage.not_included.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Not Included</h4>
                        <ul className="space-y-1.5">{selectedPackage.not_included.map((item, i) => <li key={i} className="text-gray-600 flex items-start gap-2 text-sm"><span className="text-red-400 mt-0.5">&#x2717;</span><span>{item}</span></li>)}</ul>
                      </div>
                    )}
                    {selectedPackage.notes && <div className="p-3 bg-gray-50 rounded-lg"><h4 className="font-semibold mb-1 text-gray-700 text-sm">Additional Notes</h4><p className="text-gray-600 text-sm whitespace-pre-wrap">{selectedPackage.notes}</p></div>}
                  </div>
                  <div className="shrink-0 p-4 border-t">
                    <Button onClick={handleBookPackage} className="w-full bg-amber-500 hover:bg-amber-600 text-white" size="lg" data-testid="book-special-btn">Book This Special</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* No Dive Centers Message */}
      {!selectedDiveCenter && !showSpecialsOnly && !loading && locationDiveCenters.length === 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-400" />
            <p className="text-lg text-gray-500">No packages available at this time</p>
          </div>
        </section>
      )}

      {/* Loading State */}
      {loading && (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading packages...</p>
          </div>
        </section>
      )}

      {/* Packages Section — shown when a dive center is selected */}
      {selectedDiveCenter && (
        <section className="py-16" data-testid="packages-section">
          <div className="container mx-auto px-4 max-w-7xl">
            {/* Dynamic Category Tabs */}
            {packageData && packageData.categories && (
              <>
                {/* Parent Category Tabs (excluding Extra Fees) */}
                <div className="flex justify-center mb-8">
                  <div className="inline-flex bg-white rounded-full p-1 border-2 border-gray-200 shadow-lg flex-wrap gap-1">
                    {getParentCategories().filter(cat => cat.key !== 'extra_fees' && cat.key !== 'specials').map((category) => {
                      const colorClass = COLOR_CLASSES[category.color] || COLOR_CLASSES.cyan;
                      const isActive = activeCategory === category.key;
                      
                      return (
                        <button
                          key={category.key}
                          onClick={() => {
                            setActiveCategory(category.key);
                            const subcategories = getSubcategories(category.key);
                            if (subcategories.length > 0) {
                              setActiveSubcategory(subcategories[0].key);
                            } else {
                              setActiveSubcategory(null);
                            }
                          }}
                          className={`px-6 py-3 rounded-full font-semibold transition-all ${
                            isActive
                              ? `${colorClass.bg} text-white shadow-lg`
                              : `text-gray-700 ${colorClass.hover.replace('bg-', 'hover:text-')}`
                          }`}
                          data-testid={`category-tab-${category.key}`}
                        >
                          {category.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Subcategory Tabs */}
                {activeCategory && getSubcategories(activeCategory).length > 0 && (
                  <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-gray-100 rounded-lg p-1 border border-gray-200 gap-1">
                      {getSubcategories(activeCategory).map((subcat) => {
                        const colorClass = COLOR_CLASSES[subcat.color] || COLOR_CLASSES.gray;
                        const isActive = activeSubcategory === subcat.key;
                        
                        return (
                          <button
                            key={subcat.key}
                            onClick={() => setActiveSubcategory(subcat.key)}
                            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                              isActive
                                ? `${colorClass.bg} text-white`
                                : `text-gray-600 hover:bg-gray-200`
                            }`}
                          >
                            {subcat.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Category Content */}
            {packageData && (() => {
              const hasSubcategories = activeCategory && getSubcategories(activeCategory).length > 0;
              const displayKey = (hasSubcategories && activeSubcategory) ? activeSubcategory : activeCategory;
              const displayCategory = packageData.categories?.find(c => c.key === displayKey);
              
              if (!displayKey || !displayCategory) {
                return (
                  <div className="text-center py-16 text-gray-500">
                    <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No packages available</p>
                  </div>
                );
              }

              const colorClass = COLOR_CLASSES[displayCategory.color] || COLOR_CLASSES.cyan;
              const items = packageData[displayKey] || [];

              return (
                <div className="max-w-4xl mx-auto">
                  <Card className={`bg-white border-2 ${colorClass.border} shadow-xl`}>
                    <CardHeader>
                      <div className={`w-12 h-12 ${colorClass.bg} rounded-lg flex items-center justify-center mb-4`}>
                        <Waves className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-2xl">{displayCategory.name}</CardTitle>
                      <CardDescription>
                        {items.length} {items.length === 1 ? 'option' : 'options'} available
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {items.length > 0 ? (
                        items.map((item) => {
                          const visibility = item._fieldVisibility || {};
                          
                          return (
                            <button
                              key={item.id}
                              onClick={() => setSelectedPackage({ 
                                id: item.id, 
                                name: item.name, 
                                description: item.description || item.short_description,
                                price_euro: item.price || item.price_single || item.amount,
                                ...item
                              })}
                              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                                selectedPackage?.id === item.id
                                  ? `${colorClass.border} ${colorClass.bg.replace('bg-', 'bg-')}/10`
                                  : `border-gray-200 hover:${colorClass.border} hover:bg-gray-50`
                              }`}
                              data-testid={`package-item-${item.id}`}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                                  {(visibility.description !== false && item.description) && (
                                    <p className="text-gray-600 text-sm mb-2">{item.description}</p>
                                  )}
                                  {(visibility.short_description !== false && item.short_description) && (
                                    <p className="text-gray-600 text-sm mb-2">{item.short_description}</p>
                                  )}
                                  {(visibility.duration !== false && item.duration) && (
                                    <p className="text-gray-500 text-sm">Duration: {item.duration}</p>
                                  )}
                                </div>
                                <div className="text-right ml-4">
                                  {item.price && visibility.price !== false && (
                                    <p className={`text-2xl font-bold ${colorClass.text}`}>&euro;{item.price}</p>
                                  )}
                                  {item.price_single && visibility.price_single !== false && (
                                    <p className={`text-xl font-bold ${colorClass.text}`}>
                                      &euro;{item.price_single} <span className="text-sm text-gray-500">single</span>
                                    </p>
                                  )}
                                  {item.price_double && visibility.price_double !== false && (
                                    <p className={`text-lg font-semibold ${colorClass.text}`}>
                                      &euro;{item.price_double} <span className="text-sm text-gray-500">double</span>
                                    </p>
                                  )}
                                  {item.amount && visibility.amount !== false && (
                                    <p className={`text-xl font-bold ${colorClass.text}`}>
                                      &euro;{item.amount}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {(item.full_description || item.included?.length > 0 || item.prerequisites || item.notes) && (
                                <p className={`text-xs mt-2 ${colorClass.text} font-medium`}>Click for details</p>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <p className="text-center text-gray-500 py-8">No packages available in this category</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Package Detail Modal */}
                  {selectedPackage && (
                    <div
                      className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
                      onClick={() => setSelectedPackage(null)}
                      data-testid="package-detail-modal-overlay"
                    >
                      <div
                        className={`bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col border-t-4 ${colorClass.border}`}
                        onClick={e => e.stopPropagation()}
                        data-testid="package-detail-modal"
                      >
                        {/* Header */}
                        <div className="shrink-0 p-5 border-b flex justify-between items-start">
                          <div>
                            <h2 className="text-xl font-bold text-gray-900">{selectedPackage.name}</h2>
                            <div className="flex items-center gap-3 mt-1">
                              {selectedPackage.price_euro && (
                                <span className={`text-2xl font-bold ${colorClass.text}`}>&euro;{selectedPackage.price_euro}</span>
                              )}
                              {selectedPackage.price_single && (
                                <span className={`text-lg font-bold ${colorClass.text}`}>
                                  &euro;{selectedPackage.price_single} <span className="text-sm text-gray-500">single</span>
                                </span>
                              )}
                              {selectedPackage.price_double && (
                                <span className={`text-lg font-semibold ${colorClass.text}`}>
                                  &euro;{selectedPackage.price_double} <span className="text-sm text-gray-500">double</span>
                                </span>
                              )}
                              {selectedPackage.duration && (
                                <span className="text-sm text-gray-500 border-l pl-3">{selectedPackage.duration}</span>
                              )}
                            </div>
                          </div>
                          <button onClick={() => setSelectedPackage(null)} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors" data-testid="close-package-modal">
                            <X className="w-5 h-5 text-gray-500" />
                          </button>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5">
                          {selectedPackage.full_description && (
                            <div>
                              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedPackage.full_description}</p>
                            </div>
                          )}
                          {!selectedPackage.full_description && selectedPackage.description && (
                            <div>
                              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedPackage.description}</p>
                            </div>
                          )}

                          {selectedPackage.max_depth_meters && (
                            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                              <Waves className="w-5 h-5 text-blue-500" />
                              <span className="text-sm text-blue-800">Max Depth: <strong>{selectedPackage.max_depth_meters}m</strong></span>
                              {selectedPackage.min_age && (
                                <span className="text-sm text-blue-800 border-l border-blue-200 pl-3">Min Age: <strong>{selectedPackage.min_age}+</strong></span>
                              )}
                            </div>
                          )}

                          {selectedPackage.included && selectedPackage.included.length > 0 && (
                            <div>
                              <h4 className="font-semibold mb-2 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                What's Included
                              </h4>
                              <ul className="space-y-1.5">
                                {selectedPackage.included.map((item, i) => (
                                  <li key={i} className="text-gray-600 flex items-start gap-2 text-sm">
                                    <span className="text-green-500 mt-0.5">&#x2713;</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedPackage.not_included && selectedPackage.not_included.length > 0 && (
                            <div>
                              <h4 className="font-semibold mb-2">Not Included</h4>
                              <ul className="space-y-1.5">
                                {selectedPackage.not_included.map((item, i) => (
                                  <li key={i} className="text-gray-600 flex items-start gap-2 text-sm">
                                    <span className="text-red-400 mt-0.5">&#x2717;</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {selectedPackage.prerequisites && (
                            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <h4 className="font-semibold mb-1 text-amber-800 text-sm">Prerequisites</h4>
                              <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedPackage.prerequisites}</p>
                            </div>
                          )}

                          {selectedPackage.notes && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <h4 className="font-semibold mb-1 text-gray-700 text-sm">Additional Notes</h4>
                              <p className="text-gray-600 text-sm whitespace-pre-wrap">{selectedPackage.notes}</p>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="shrink-0 p-4 border-t">
                          <Button 
                            onClick={handleBookPackage}
                            className={`w-full ${colorClass.bg} ${colorClass.hover} text-white`}
                            size="lg"
                            data-testid="book-package-btn"
                          >
                            Book This Package
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {!packageData && selectedDiveCenter && (
              <div className="text-center py-16 text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Loading packages...</p>
              </div>
            )}

            {/* George Dives Specials — collapsed section when viewing a dive center */}
            {locationSpecials.length > 0 && (
              <div className="mt-16 pt-12 border-t-2 border-amber-200" data-testid="specials-in-center-section">
                <button
                  type="button"
                  onClick={() => setSpecialsExpanded(prev => !prev)}
                  className="w-full max-w-4xl mx-auto flex items-center justify-between gap-4 px-5 py-4 rounded-xl border-2 border-amber-300 bg-amber-50/70 hover:bg-amber-50 transition-colors"
                  data-testid="specials-in-center-trigger"
                  aria-expanded={specialsExpanded}
                >
                  <div className="flex items-center gap-3 text-left">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">George Dives Specials</p>
                      <p className="text-sm text-gray-500">{locationSpecials.length} exclusive offer{locationSpecials.length !== 1 ? 's' : ''} at {currentLocationInfo?.name}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-5 h-5 text-amber-600 transition-transform duration-200 flex-shrink-0" style={{ transform: specialsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </button>

                {specialsExpanded && (
                <div className="max-w-4xl mx-auto mt-4">
                  <Card className="border-2 border-amber-400 shadow-xl" style={{ backgroundColor: isDarkTheme ? "#1c1533" : "#ffffff" }}>
                    <CardHeader>
                      <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-4">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-2xl">George Dives Specials</CardTitle>
                      <CardDescription>{locationSpecials.length} offer{locationSpecials.length !== 1 ? 's' : ''} available</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {locationSpecials.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => setSelectedPackage({
                            id: item.id,
                            name: item.name,
                            description: item.description || item.short_description,
                            price_euro: item.price || item.price_single || item.amount,
                            _isSpecial: true,
                            ...item
                          })}
                          className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                            selectedPackage?.id === item.id
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-gray-200 hover:border-amber-400 hover:bg-gray-50'
                          }`}
                          data-testid={`special-item-${item.id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-bold text-lg mb-1">{item.name}</h3>
                              {item.description && <p className="text-gray-600 text-sm mb-2">{item.description}</p>}
                              {item.duration && <p className="text-gray-500 text-sm">Duration: {item.duration}</p>}
                            </div>
                            <div className="text-right ml-4">
                              {item.price && <p className="text-2xl font-bold text-amber-600">&euro;{item.price}</p>}
                              {item.price_single && <p className="text-xl font-bold text-amber-600">&euro;{item.price_single} <span className="text-sm text-gray-500">single</span></p>}
                              {item.price_double && <p className="text-lg font-semibold text-amber-600">&euro;{item.price_double} <span className="text-sm text-gray-500">double</span></p>}
                            </div>
                          </div>
                          {(item.full_description || item.included?.length > 0 || item.notes) && (
                            <p className="text-xs mt-2 text-amber-600 font-medium">Click for details</p>
                          )}
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                </div>
                )}
              </div>
            )}

            {/* Extra Fees Section */}
            {packageData && packageData.extra_fees && packageData.extra_fees.length > 0 && (
              <div className="mt-16 pt-12 border-t-2 border-gray-200">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Extra Fees & Add-ons</h2>
                  <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 max-w-3xl mx-auto">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm font-medium">
                      All extra costs in local currency ({packageData.extra_fees[0]?.currency || 'PHP'}) are paid separately directly at the dive center
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                  {packageData.extra_fees.map((fee) => (
                    <Card key={fee.id} className="border-2 border-gray-200 hover:border-slate-400 transition-all">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">{fee.name}</h3>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-600">
                              {fee.currency === 'PHP' && '\u20B1'}
                              {fee.currency === 'EUR' && '\u20AC'}
                              {fee.currency === 'USD' && '$'}
                              {!['PHP', 'EUR', 'USD'].includes(fee.currency) && fee.currency + ' '}
                              {fee.amount}
                            </p>
                          </div>
                        </div>
                        {fee.notes && (
                          <p className="text-sm text-gray-600 italic">{fee.notes}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <Footer />
    </>
  );
};

export default Packages;
